import * as React from 'react';
import { WebAPI } from '../../../MedicareProAPI';
import StyledModal from '../StyledModal';

/**
 * Component for managing Twilio integration
 * 
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2026-01-14
 * @lastUpdated
 * @version 1.0.0
 */

// --- Types -----------------------------------------------------------------
interface TwilioProps {
  userId: number;
  userAccessToken: string;
  id: string;
  name: string;
  image: string;
  imageHeight: string;
  databaseId: number;
  active: boolean;
  type: string;
  external: boolean;
  loading: boolean;
  htmlInnerSuffix: string;
  token: string;
  accountId: string;
  tokenExists: boolean;
  tokenSaving: boolean;
  tokenRevoking: boolean;
  tokenRevoked: boolean;
  onToken: (token) => void;
  onAccountId: (accountId) => void;
  onPATSave: () => void;
  onRevoke: () => void;
}

type TwilioPhonesResponse = {
  phoneNumberId: string;
  phoneNumberName: string;
  phoneNumber: string;
  webhookURL: string | null;
}


// --- Component --------------------------------------------------------------
export const Twilio = ({
  userId,
  userAccessToken,
  id,
  name,
  image,
  imageHeight,
  databaseId,
  active,
  type,
  external,
  loading,
  htmlInnerSuffix,
  token,
  accountId,
  tokenExists,
  tokenSaving,
  tokenRevoking,
  tokenRevoked,
  onToken,
  onAccountId,
  onPATSave,
  onRevoke }: TwilioProps): JSX.Element => {


  // --- State ---------------------------------------------------------------
  const [show, setShow] = React.useState<boolean>(false);
  const [showEdit, setShowEdit] = React.useState<boolean>(false);
  const [showPhoneNumbers, setShowPhoneNumbers] = React.useState<boolean>(false);
  const [fetchingNumbers, setFetchingNumbers] = React.useState<boolean>(false);
  const [twilioNumbers, setTwilioNumbers] = React.useState<Array<TwilioPhonesResponse>>([]);
  const [addingId, setAddingId] = React.useState<string>(null);
  const [removingId, setRemovingId] = React.useState<string>(null);
  const [errorId, setErrorId] = React.useState<string>(null);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  // --- Derived values ------------------------------------------------------
  const webAPI = React.useMemo(() => {
    const r = new WebAPI();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);


  // --- Data loading --------------------------------------------------------
  const getTwilioPhoneNumbers = async () => {
    setShowPhoneNumbers(true);
    setFetchingNumbers(true);
    const phoneNumbers: Array<TwilioPhonesResponse> = await webAPI.getUserIntegrationPatVoipPhoneNumbers(userId, 'twilio');
    setFetchingNumbers(false)
    if (phoneNumbers.length > 0) {
      setTwilioNumbers(phoneNumbers);
    }

  }

  const addPhoneNumber = async (phone: TwilioPhonesResponse, userId: number) => {
    const savePayload = {
      app: databaseId,
      phoneNumber: phone.phoneNumber,
      phoneNumberName: phone.phoneNumberName,
      phoneNumberId: phone.phoneNumberId,
    };

    const subscribePayload = {
      phoneNumberId: phone.phoneNumberId,
    };

    let savedPhone: any = null;

    try {

      // 1) Create the phone record in the database
      savedPhone = await webAPI.createUserVoipPhoneNumbers(userId, savePayload);

      // 2) Subscribe the webhook (if this fails, rollback)
      await webAPI.subscribeUserVoipWebhooks(id, subscribePayload, type);

      // 3) Update UI state on success
      setTwilioNumbers(prev =>
        prev.map(item =>
          item.phoneNumberId === phone.phoneNumberId
            ? { ...item, webhookURL: "service.medicareproapp.com" }
            : item
        )
      );
    } catch (err) {

      setErrorId(phone.phoneNumberId);
      // delete the previously created phone record
      try {
        await removePhoneNumber(phone, userId);
      } catch (rollbackErr) {

        console.error("Rollback delete failed", rollbackErr);
      }


      throw err;
    } finally {
      setAddingId(null);
    }
  }

  const removePhoneNumber = async (phone: TwilioPhonesResponse, userId: number) => {
    const payload = {
      app: databaseId,
      phoneNumberId: phone.phoneNumberId
    };

    try {

      await webAPI.unsubscribeUserVoipWebhooks(id, type, payload);

      await webAPI.deleteUserVoipPhoneNumbers(userId, databaseId, payload);

      setRemovingId(null);
      setTwilioNumbers(prev =>
        prev.map(item =>
          item.phoneNumberId === phone.phoneNumberId
            ? { ...item, webhookURL: "" }
            : item
        )
      );

    } catch (err) {
      setErrorId(phone.phoneNumberId);
    } finally {
      setRemovingId(null);
    }

  }

  const deleteSubscriptions = () => {

    webAPI.unsubscribeUserVoipWebhooks(id, type);

    webAPI.deleteUserVoipPhoneNumbers(userId, databaseId);
  }


  // --- Event handlers ------------------------------------------------------
  const handleSave = async () => {

    setShow(false)
    onPATSave();
    setIsSaving(true);
    setShowPhoneNumbers(true);

  }

  const handleRevoke = () => {
    setShowEdit(false)
    onRevoke();
    deleteSubscriptions();
  }

  const handleAddPhone = (phone: TwilioPhonesResponse, userId: number) => {
    setAddingId(phone.phoneNumberId);
    setErrorId(null);
    addPhoneNumber(phone, userId);

  }

  const handleRemovePhone = (phone: TwilioPhonesResponse, userId: number) => {
    setRemovingId(phone.phoneNumberId);
    setErrorId(null);
    removePhoneNumber(phone, userId);
  }

  // --- Side effects / subscriptions ---------------------------------------
  React.useEffect(() => {
    console.log('tokentoken: ', tokenExists);
    if (tokenExists && isSaving) {
      getTwilioPhoneNumbers()
    }

  }, [tokenExists])


  // --- Main render ---------------------------------------------------------
  return (
    <>
      <div className="row px-3">
        <div className="col-lg-6 text-center px-2 sm-resp mt-4">
          {
            tokenExists ?
              <>
                <button
                  className="btn btn-primary mb-4 ml-1 text-white"
                  disabled
                >
                  Connected
                      </button>
                <button
                  className="btn btn-primary mb-4 ml-1 text-white"
                  onClick={() => setShowEdit(true)}
                >
                  Edit
                      </button>
              </>
              :
              <>
                <button
                  className="btn btn-primary mb-4 ml-1 text-white"
                  onClick={() => setShow(true)}
                >
                  Connect
                      </button>
              </>
          }
          {
            tokenExists &&
            <div>
              <div className="badge badge-secondary p-2" onClick={getTwilioPhoneNumbers}><i className="far fa-cog mr-1"></i>Manage Phones</div>
            </div>
          }
          {
            htmlInnerSuffix
          }

        </div>


        <div className="col-lg-6  text-center px-2   sm-resp mt-4" >
          {
            image &&
            <img className="rounded" height={imageHeight} src={'/public/img/site/' + image} />
          }
        </div>
      </div>

      {/* create */}
      <StyledModal size="lg" title="Personal Access Token" footerCloseButton={true} show={show} onClose={() => { setShow(false) }}>
        <>
          <div className="col-12">
            <label className="label">Please paste your Twilio token.</label>
            <input type="text" className="form-control form-control-sm" value={token} onChange={(e) => onToken(e.target.value)} />
          </div>
          <div className="col-12 mt-2">
            <label className="label">Please paste your Twilio SID.</label>
            <input type="text" className="form-control form-control-sm" value={accountId} onChange={(e) => onAccountId(e.target.value)} />
          </div>
          <div className="col-12 mt-3">
            <button className="btn btn-sm btn-primary" disabled={tokenSaving} onClick={handleSave} >Save</button>
          </div>
        </>
      </StyledModal>

      {/* edit */}
      <StyledModal size="lg" title="Edit Personal Access Token" footerCloseButton={true} show={showEdit} onClose={() => { setShowEdit(false); setTwilioNumbers([]) }}>
        {
          tokenRevoking &&
          <div className="alert small alert-info" role="alert"><i className="fas fa-spinner fa-spin ml-2" ></i> Removing your token</div>
        }
        {
          (tokenRevoked && !tokenRevoking) &&
          <p className="alert alert-success small ">Your access token has been revoked</p>
        }

        {
          (!tokenRevoked && !tokenRevoking) &&
          <>
            <div className="row">
              <div className="col col-8">
                <p className="small calMess">Adding a new access token will not affect service as long as the token is valid.</p>
              </div>
              <div className="col col-4 text-right"><button onClick={() => { setShow(true), setShowEdit(false) }} className="btn  btn-primary mb-4 text-white">Refresh Token</button></div>

              <div className="col col-8"><p className="small calMess">Revoking your access token will mean MedicarePro will no longer be able to access the service.</p></div>
              <div className="col col-4 text-right"><button onClick={handleRevoke} className="btn  btn-danger mb-4 text-white">Revoke Token</button></div>
            </div>
          </>
        }
      </StyledModal>

      {/* phone numbers */}
      <StyledModal size="lg" title="Twilio Phone Numbers" footerCloseButton={true} show={showPhoneNumbers} onClose={() => { setShowPhoneNumbers(false) }}>

        <div className="col-12 mt-2">
          {fetchingNumbers && <div><i className="far fa-spinner fa-spin"></i> Fetching your Twilio numbers...</div>}
          <>
            {(!fetchingNumbers && twilioNumbers.length !== 0) && (
              <p>
                Please select the phone numbers you want to use or remove from the CRM.
              </p>
            )}

            {twilioNumbers.map((item, index) => {
              const id = item.phoneNumberId;
              const isAdding = addingId === id;
              const isRemoving = removingId === id;
              const isError = errorId === id;

              return (
                <div
                  key={`twilio-phone-${id ?? index}`}
                  className="bg-contrast calMess rounded p-2 mb-2"
                >
                  <i className="far fa-phone mr-2 text-danger cp"></i>
                  {item.phoneNumberName}

                  {item.webhookURL?.includes('service.medicareproapp.com') ? (
                    <>
                      <span className="badge border border-success badge-success ml-4">
                        <i className="far fa-check"></i>
                      </span>
                      <span className="badge border border-danger text-danger ml-2 cp" onClick={() => handleRemovePhone(item, userId)}>
                        {isRemoving ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Removing…
                                  </>
                        ) : (
                          'Remove'
                        )}
                      </span>
                    </>
                  ) : (
                    <span
                      className={`badge border ml-4 ${isAdding ? 'border-secondary text-secondary' : 'border-success text-success cp'}`}
                      onClick={() => !isAdding && handleAddPhone(item, userId)}
                      style={{ pointerEvents: isAdding ? 'none' : 'auto' }}
                    >
                      {isAdding ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Adding…
                        </>
                      ) : (
                        'Add'
                      )}
                    </span>
                  )}
                  {
                    isError &&
                    <span className="text-danger ml-2">Error saving phone</span>
                  }
                </div>
              );
            })}
          </>
        </div>

      </StyledModal>


    </>
  );
};





